import { LoadingState } from "@/components/ui/loading-state";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { MeetingNotesDialog } from "@/components/MeetingNotesDialog";
import { downloadDatabookQnaExcel } from "@/lib/api/databook";
import {
  createFddQaQuestion,
  generateDealImplications,
  getQnaQuestions,
  reviewQnaAnswer,
  updateFddQaQuestion,
  type QnaRegister,
} from "@/lib/api/fddQa";
import { QnaQuestionsList } from "./QnaQuestionsList";
import { qnaActionStyle } from "./qnaStyles";

interface Props {
  projectId: string;
  focusedQuestionId?: string | null;
  canEdit: boolean;
  onBack: () => void;
  onOpenSheet: (sheet: string, cellAddresses: string[], versionId: string | null) => void;
}

export function DatabookQnaStage({
  projectId,
  focusedQuestionId,
  canEdit,
  onBack,
  onOpenSheet,
}: Props) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryKey = ["databook-qna-register", projectId];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => getQnaQuestions(projectId, signal),
    enabled: Boolean(projectId),
    retry: false,
  });
  async function action(run: () => Promise<unknown>, reportError = true) {
    setBusy(true);
    setError(null);
    try {
      await run();
      await Promise.all([
        client.invalidateQueries({ queryKey }),
        client.invalidateQueries({
          queryKey: ["databook-insights", projectId],
        }),
      ]);
    } catch (failure) {
      if (reportError) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Could not complete the action.",
        );
      }
      throw failure;
    } finally {
      setBusy(false);
    }
  }
  if (query.isPending) return <LoadingState />;

  const questions = query.data?.questions ?? [];
  return (
    <section
      aria-label="Q&A"
      className="min-h-0 flex-1 overflow-auto"
    >
      <div className="builder-stage-frame space-y-5 py-5 lg:py-8">
        {error && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-lg border p-3 text-sm text-red-600"
          >
            {error}
            <Button
              className="text-[12.5px] font-medium"
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
            >
              Reload Q&A
            </Button>
          </div>
        )}
        {query.isError ? (
          <div role="alert">
            Q&A could not be loaded.{" "}
            <button className="text-[12.5px] font-medium underline" onClick={() => void query.refetch()}>
              Retry
            </button>
          </div>
        ) : (
          <QnaQuestionsList
            questions={questions}
            focusedQuestionId={focusedQuestionId}
            canEdit={canEdit}
            busy={busy}
            reviewingId={reviewingId}
            sheetNames={query.data?.sheet_names}
            onOpenSheet={(sheet, cells) => onOpenSheet(sheet, cells, query.data?.databook_version_id ?? null)}
            actions={
              <>
                {canEdit && (
                  <MeetingNotesDialog
                    projectId={projectId}
                    disabled={busy || !questions.length}
                    onApply={(match) =>
                      action(() =>
                        updateFddQaQuestion(
                          projectId,
                          { id: match.question_id, revision: match.revision },
                          { answer: match.answer },
                        ),
                      )
                    }
                  />
                )}
                <button
                  type="button"
                  style={qnaActionStyle}
                  className="disabled:opacity-50"
                  disabled={
                    busy || !questions.some((q) => q.status !== "Rejected")
                  }
                  onClick={() =>
                    void action(() =>
                      downloadDatabookQnaExcel(projectId),
                    ).catch(() => {})
                  }
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="1"
                      y="1"
                      width="14"
                      height="14"
                      rx="2"
                      fill="#217346"
                    />
                    <text
                      x="8"
                      y="11.5"
                      textAnchor="middle"
                      fontSize="9"
                      fontWeight="700"
                      fill="#fff"
                    >
                      X
                    </text>
                  </svg>
                  Download Q&A
                </button>
              </>
            }
            onAdd={(question) =>
              action(() => createFddQaQuestion(projectId, question))
            }
            onContinue={() =>
              action(() => generateDealImplications(projectId))
            }
            onUpdate={(question, update) =>
              action(() =>
                updateFddQaQuestion(projectId, question, update),
              )
            }
            onSubmitAnswer={async (question, answer) => {
              setReviewingId(question.id);
              try {
                await action(async () => {
                  // Review the saved revision, including after a failed check is retried.
                  const saved = answer === question.answer
                    ? question
                    : await updateFddQaQuestion(projectId, question, { answer });
                  const publish = (updated: typeof saved) => {
                    client.setQueryData<QnaRegister>(queryKey, (current) => current && ({
                      ...current,
                      questions: current.questions.map((item) => item.id === updated.id ? updated : item),
                    }));
                  };
                  publish(saved);
                  publish(await reviewQnaAnswer(projectId, saved));
                }, false);
              } finally {
                setReviewingId(null);
              }
            }}
          />
        )}
        <Button variant="outline" className="text-[12.5px] font-medium" onClick={onBack}>
          ← Databook
        </Button>
      </div>
    </section>
  );
}
